import { Component, OnInit, computed, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { TruckAnnouncementService } from '../../core/api/truck-announcement.service';
import { TruckAnnouncementListItem } from '../../shared/models/truck-announcement';
import { statusTone } from '../../shared/status-tone';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';
import { StatusLabelPipe } from '../../shared/status-label.pipe';
import { MneDatePipe } from '../../shared/mne-date.pipe';

type ViewMode = 'card' | 'table';

@Component({
  standalone: true,
  imports: [RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe, StatusLabelPipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ 'ta.title' | t }}</h1>
        <a class="icon-action" routerLink="/truck-announcements/new" [attr.aria-label]="'ta.new' | t">＋</a>
      </header>
      <div class="search-bar">
        <input type="search" [placeholder]="'common.search' | t" [value]="query()" (input)="query.set($any($event.target).value)">
      </div>

      <div class="filter-bar">
        <select [value]="statusFilter()" (change)="statusFilter.set($any($event.target).value)">
          <option value="">{{ 'filters.allStatuses' | t }}</option>
          @for (s of statusOptions(); track s) { <option [value]="s">{{ s | statusLabel }}</option> }
        </select>
        <select [value]="operatorFilter()" (change)="operatorFilter.set($any($event.target).value)">
          <option value="">{{ 'filters.allOperators' | t }}</option>
          @for (o of operatorOptions(); track o) { <option [value]="o">{{ o }}</option> }
        </select>
        @if (statusFilter() || operatorFilter()) {
          <button type="button" class="filter-clear" (click)="clearFilters()">{{ 'filters.clear' | t }}</button>
        }
      </div>

      <button type="button" class="view-toggle" (click)="toggleView()">
        @if (viewMode() === 'card') {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="4" rx="1"/><rect x="3" y="10" width="18" height="4" rx="1"/><rect x="3" y="16" width="18" height="4" rx="1"/></svg>
          {{ 'ta.viewTable' | t }}
        } @else {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/></svg>
          {{ 'ta.viewCards' | t }}
        }
      </button>

      @if (loading()) {
        <div class="skeleton-list">
          @for (i of [1,2,3,4,5,6]; track i) { <div class="skeleton-row"></div> }
        </div>
      }
      @if (error()) { <p class="error notice">{{ error() | t }}</p> }

      @if (!loading() && viewMode() === 'card') {
        <section class="list">
          @for (item of filtered(); track item.Id) {
            <div class="time-card" [class]="'tone-' + tone(item.TruckAnnouncementStatusName)">
              <div class="time-card-top">
                <span class="row-id">#{{ item.Id }}</span>
                <span class="status-pill" [class]="tone(item.TruckAnnouncementStatusName)">{{ item.TruckAnnouncementStatusName | statusLabel }}</span>
                <a class="row-edit" [routerLink]="['/truck-announcements', item.Id, 'edit']" [attr.aria-label]="'common.edit' | t">✎</a>
              </div>
              <span class="time-card-ref">{{ item.TerminalOperatorName || ('ta.fallback' | t) }}</span>
              @if (item.ParkingCargoDescriptionName || item.Description) {
                <div class="time-card-cargo">{{ item.ParkingCargoDescriptionName || item.Description }}</div>
              }
              <div class="time-card-times">
                <div class="time-block">
                  <span class="time-value">{{ timeOf(item.Id, 'from') }}</span>
                  <span class="time-date">{{ dateOf(item.Id, 'from') }}</span>
                </div>
                <div class="time-card-sep">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                </div>
                <div class="time-block">
                  <span class="time-value">{{ timeOf(item.Id, 'to') }}</span>
                  <span class="time-date">{{ dateOf(item.Id, 'to') }}</span>
                </div>
              </div>
              <a class="time-card-more" [routerLink]="['/truck-announcements', item.Id]">{{ 'ta.seeMore' | t }} ›</a>
            </div>
          } @empty { @if (!error()) {
            <div class="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 7h16v13H4z"/><path d="M4 7l3-4h10l3 4"/><path d="M9 11h6"/></svg>
              <span>{{ 'ta.empty' | t }}</span>
            </div>
          } }
        </section>
      }

      @if (!loading() && viewMode() === 'table' && filtered().length) {
        <div class="data-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ 'ta.terminalOperator' | t }}</th>
                <th>{{ 'ta.cargoDescription' | t }}</th>
                <th>{{ 'ta.announcementFrom' | t }} – {{ 'ta.announcementTo' | t }}</th>
                <th>{{ 'common.status' | t }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (item of filtered(); track item.Id) {
                <tr [class]="'tone-' + tone(item.TruckAnnouncementStatusName)" [routerLink]="['/truck-announcements', item.Id]">
                  <td class="cell-id">#{{ item.Id }}</td>
                  <td>{{ item.TerminalOperatorName || ('ta.fallback' | t) }}</td>
                  <td class="cell-muted">{{ item.ParkingCargoDescriptionName || item.Description || '—' }}</td>
                  <td class="cell-muted">
                    @if (dateRangeText(item.Id); as range) { {{ range }} }
                    @else if (loadingDates()) { <span class="row-dates-loading"></span> }
                    @else { — }
                  </td>
                  <td><span class="status-pill" [class]="tone(item.TruckAnnouncementStatusName)">{{ item.TruckAnnouncementStatusName | statusLabel }}</span></td>
                  <td class="cell-actions">
                    <a class="row-edit" [routerLink]="['/truck-announcements', item.Id, 'edit']" (click)="$event.stopPropagation()" [attr.aria-label]="'common.edit' | t">✎</a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else if (!loading() && viewMode() === 'table' && !error()) {
        <div class="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 7h16v13H4z"/><path d="M4 7l3-4h10l3 4"/><path d="M9 11h6"/></svg>
          <span>{{ 'ta.empty' | t }}</span>
        </div>
      }
      <a class="fab" routerLink="/truck-announcements/new">＋</a>
    </main>
  `
})
export class TruckAnnouncementsPage implements OnInit {
  readonly items = signal<TruckAnnouncementListItem[]>([]);
  readonly loading = signal(true);
  readonly loadingDates = signal(false);
  readonly error = signal('');
  readonly query = signal('');
  readonly statusFilter = signal('');
  readonly operatorFilter = signal('');
  readonly viewMode = signal<ViewMode>(this.readStoredView());
  private readonly dates = signal<Map<number, { from?: string | null; to?: string | null }>>(new Map());
  private readonly datePipe = new MneDatePipe();

  readonly statusOptions = computed(() => this.distinct(this.items().map(i => i.TruckAnnouncementStatusName)));
  readonly operatorOptions = computed(() => this.distinct(this.items().map(i => i.TerminalOperatorName)));

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const status = this.statusFilter();
    const operator = this.operatorFilter();
    return this.items().filter(item => {
      if (status && item.TruckAnnouncementStatusName !== status) return false;
      if (operator && item.TerminalOperatorName !== operator) return false;
      if (!q) return true;
      return String(item.Id).includes(q) ||
        (item.TerminalOperatorName ?? '').toLowerCase().includes(q) ||
        (item.Description ?? '').toLowerCase().includes(q) ||
        (item.ParkingCargoDescriptionName ?? '').toLowerCase().includes(q) ||
        (item.TruckAnnouncementStatusName ?? '').toLowerCase().includes(q);
    });
  });

  clearFilters(): void {
    this.statusFilter.set('');
    this.operatorFilter.set('');
  }

  private distinct(values: (string | null | undefined)[]): string[] {
    return Array.from(new Set(values.filter((v): v is string => !!v))).sort();
  }

  constructor(private readonly api: TruckAnnouncementService) {}

  ngOnInit(): void {
    this.api.search({ Page: 1, PageSize: 50 }).subscribe({
      next: items => { this.items.set(items); this.loading.set(false); this.loadDateRanges(items); },
      error: err => { this.error.set(err?.error?.Message ?? err?.error?.message ?? 'ta.loadError'); this.loading.set(false); }
    });
  }

  tone(status?: string | null): string { return statusTone(status); }

  toggleView(): void {
    const next: ViewMode = this.viewMode() === 'card' ? 'table' : 'card';
    this.viewMode.set(next);
    localStorage.setItem('ta.viewMode', next);
  }

  timeOf(id: number, which: 'from' | 'to'): string {
    const iso = this.dates().get(id)?.[which];
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  dateOf(id: number, which: 'from' | 'to'): string {
    const iso = this.dates().get(id)?.[which];
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  dateRangeText(id: number): string {
    const d = this.dates().get(id);
    if (!d || (!d.from && !d.to)) return '';
    const from = d.from ? this.datePipe.transform(d.from, false) : '—';
    const to = d.to ? this.datePipe.transform(d.to, false) : '—';
    return from === to ? from : `${from} – ${to}`;
  }

  private readStoredView(): ViewMode {
    return localStorage.getItem('ta.viewMode') === 'table' ? 'table' : 'card';
  }

  private loadDateRanges(items: TruckAnnouncementListItem[]): void {
    this.loadingDates.set(true);
    from(items).pipe(
      mergeMap(item => this.api.get(item.Id).pipe(
        map(detail => ({ id: item.Id, from: detail.AnnouncementFrom, to: detail.AnnouncementTo })),
        catchError(() => of(null))
      ), 6)
    ).subscribe({
      next: result => {
        if (!result) return;
        const map = new Map(this.dates());
        map.set(result.id, { from: result.from, to: result.to });
        this.dates.set(map);
      },
      complete: () => this.loadingDates.set(false)
    });
  }
}
